import { useEffect } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import { isNative, isIOS } from '../services/platformService';
import { recordAppOpen, shouldPromptReview } from '../services/reviewPromptService';
import { useReviewPrompt } from '../contexts/ReviewPromptContext';

/**
 * Hook to initialise native-specific features on iOS/Android
 *
 * This handles:
 * - Splash screen management
 * - Status bar styling
 * - Safe area handling
 * - Platform-specific UI adjustments
 */
export const useNativeInit = () => {
  const { open: openReviewPrompt } = useReviewPrompt();

  useEffect(() => {
    if (!isNative()) return;

    const initNative = async () => {
      try {
        // Add platform class to html and body for CSS targeting
        if (isIOS()) {
          document.documentElement.classList.add('native-ios');
          document.body.classList.add('native-ios');
        } else {
          document.body.classList.add('native-android');
        }

        // Status bar style is owned by AppearanceContext (calls updateStatusBarForTheme
        // on every theme change). Don't set a default here or it'll briefly override
        // the user's theme on launch.

        // Hide splash screen once app is ready
        // This provides a smoother transition than auto-hide
        await SplashScreen.hide({
          fadeOutDuration: 500
        });

        // Count this launch toward the review-prompt eligibility window.
        await recordAppOpen();

        console.log('📱 Native initialisation complete');
      } catch (error) {
        console.error('📱 Error initialising native features:', error);
      }
    };

    initNative();

    // Re-check review-prompt eligibility when the app comes back to the foreground.
    // We avoid prompting on cold-boot itself - the user hasn't done anything yet.
    // When the gates pass, open our friendly modal — the native sheet only
    // fires if the user taps Yes inside the modal.
    let resumeHandle;
    (async () => {
      try {
        resumeHandle = await CapApp.addListener('appStateChange', async ({ isActive }) => {
          if (!isActive) return;
          try {
            if (await shouldPromptReview()) openReviewPrompt();
          } catch (err) {
            console.warn('📱 shouldPromptReview failed:', err);
          }
        });
      } catch (err) {
        console.warn('📱 Failed to attach appStateChange listener:', err);
      }
    })();

    // Cleanup: remove classes on unmount
    return () => {
      document.documentElement.classList.remove('native-ios');
      document.body.classList.remove('native-ios', 'native-android');
      if (resumeHandle?.remove) resumeHandle.remove();
    };
    // openReviewPrompt is stable (useCallback in the provider), so a single
    // mount-only effect is fine — listing it here documents the dep without
    // re-running the heavy init on identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * Update status bar style based on current theme
 * Call this when theme changes
 */
export const updateStatusBarForTheme = async (isDarkMode) => {
  if (!isNative()) return;

  try {
    if (isIOS()) {
      // iOS: Style.Light = dark text (for light backgrounds)
      // Style.Dark = light text (for dark backgrounds)
      await StatusBar.setStyle({
        style: isDarkMode ? Style.Dark : Style.Light
      });
    } else {
      // Android: Set both style and background colour
      await StatusBar.setStyle({
        style: isDarkMode ? Style.Light : Style.Dark
      });
      await StatusBar.setBackgroundColor({
        color: isDarkMode ? '#1a1a2e' : '#ffffff'
      });
    }
  } catch (error) {
    console.error('📱 Error updating status bar:', error);
  }
};

export default useNativeInit;
